import React, { useState } from 'react';
import { BarChart3 } from 'lucide-react';
import { apiCall } from '../api';
import { useMountEffect } from '../hooks/useMountEffect';

const UsageReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useMountEffect(() => {
    const fetchReport = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError('Please login to access this page');
          setLoading(false);
          return;
        }

        const reportData = await apiCall('/api/usage-report');
        setData(reportData);
      } catch (err) {
        console.error('Error fetching usage report:', err);
        setError('Failed to load usage report');
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  });

  const formatTime = (seconds) => {
    if (!seconds || seconds <= 0) return '-';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    if (mins < 60) return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
    const hrs = Math.floor(mins / 60);
    const remainMins = mins % 60;
    return `${hrs}h ${remainMins}m`;
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem' }}>
        <h1><BarChart3 size={24} style={{ display: 'inline', marginRight: '1rem' }} />Usage Report</h1>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '40vh', gap: '1rem' }}>
          <div style={{ width: 40, height: 40, border: '4px solid #e5e7eb', borderTop: '4px solid #667eea', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <p style={{ color: '#6b7280' }}>Loading usage report...</p>
        </div>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ padding: '2rem' }}>
        <h1><BarChart3 size={24} style={{ display: 'inline', marginRight: '1rem' }} />Usage Report</h1>
        <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', padding: '1.5rem', borderRadius: '8px', marginTop: '2rem', color: '#991b1b', textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: '1rem' }}>{error || 'No data available'}</p>
        </div>
      </div>
    );
  }

  const pipelines = data.pipelines || {};
  const pipelineList = [
    { key: 'glib', name: 'GLIB (Main)', color: '#667eea', bgColor: '#eef2ff' },
    { key: 'v1', name: 'XOrbit / VJA', color: '#8b5cf6', bgColor: '#f5f3ff' },
    { key: 'cocb', name: 'COCB', color: '#f59e0b', bgColor: '#fffbeb' },
    { key: 'gfs', name: 'GFS', color: '#10b981', bgColor: '#ecfdf5' }
  ];

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ marginBottom: '0.5rem' }}><BarChart3 size={24} style={{ display: 'inline', marginRight: '1rem' }} />Usage Report</h1>
      <p style={{ color: '#6b7280', marginBottom: '2rem', fontSize: '0.95rem' }}>Combined statistics across all pipelines</p>

      {/* Combined Overview */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '2.5rem' }}>
        <div style={{ padding: '1.5rem', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '12px', color: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '0.8rem', opacity: 0.9, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Users</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, marginTop: '0.5rem' }}>{data.totalUsers}</div>
        </div>
        <div style={{ padding: '1.5rem', background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', borderRadius: '12px', color: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '0.8rem', opacity: 0.9, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Active Users</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, marginTop: '0.5rem' }}>{data.activeUsers}</div>
        </div>
        <div style={{ padding: '1.5rem', background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', borderRadius: '12px', color: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '0.8rem', opacity: 0.9, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Extracted</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, marginTop: '0.5rem' }}>{data.totalExtracted}</div>
        </div>
        <div style={{ padding: '1.5rem', background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', borderRadius: '12px', color: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '0.8rem', opacity: 0.9, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Success Rate</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, marginTop: '0.5rem' }}>{data.successRate}%</div>
        </div>
        <div style={{ padding: '1.5rem', background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', borderRadius: '12px', color: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '0.8rem', opacity: 0.9, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Avg Processing Time</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, marginTop: '0.5rem' }}>{formatTime(data.averageProcessingTime)}</div>
        </div>
        <div style={{ padding: '1.5rem', background: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)', borderRadius: '12px', color: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '0.8rem', opacity: 0.9, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Work Orders</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, marginTop: '0.5rem' }}>{data.totalWorkOrders}</div>
        </div>
      </div>

      {/* Per Pipeline Breakdown */}
      <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1f2937', marginBottom: '1.5rem' }}>Pipeline Breakdown</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
        {pipelineList.map(pl => {
          const p = pipelines[pl.key];
          if (!p) return null;
          return (
            <div key={pl.key} style={{ background: 'white', border: `1px solid ${pl.color}22`, borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', borderTop: `4px solid ${pl.color}` }}>
              <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: 700, color: pl.color }}>{pl.name}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase' }}>Work Orders</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937' }}>{p.totalWorkOrders}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase' }}>Completed</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#10b981' }}>{p.completed}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase' }}>Success Rate</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: pl.color }}>{p.successRate}%</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase' }}>Avg Time</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#6b7280' }}>{formatTime(p.avgProcessingTime)}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase' }}>Failed</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#ef4444' }}>{p.failed}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase' }}>Pending</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#f59e0b' }}>{p.pending + p.processing}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UsageReport;
